import React, { MouseEventHandler, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import Button from 'components/ui/Button';
import ArrowDownIcon from 'components/ui/icons/ArrowDown';
import useDeviceType from 'providers/DeviceType/hooks';

import { HorizontalListOptionRef, HorizontalListOptionType, List } from '../_components';

import styles from './Dropdown.module.scss';

export type DropdownProps = {
	options: HorizontalListOptionType[];
	onChange: (option: string, name: string) => void;
	name: string;
	value?: string;
	className?: string;
	error?: string;
	label?: string | JSX.Element;
	placeholder?: string;
	searchable?: boolean;
	centerOptions?: boolean;
	position?: 'top' | 'bottom' | 'right' | 'left';
	size?: 'x-small' | 'small' | 'default' | 'large' | 'x-large';
	withHidden?: boolean;
	subLabel?: string;
};

export const Dropdown: React.FC<DropdownProps> = ({
	name,
	options: _options,
	label,
	onChange,
	value,
	error,
	className,
	placeholder,
	searchable,
	position,
	size,
	subLabel,
	centerOptions,
	withHidden = true,
}) => {
	const { isMobile } = useDeviceType();

	const [isOpen, setIsOpen] = useState<boolean>(false);

	const listRef = useRef<null | HorizontalListOptionRef>(null);

	const options = useMemo(() => {
		if (withHidden) return _options;
		return _options?.filter((o) => !o.hidden);
	}, [withHidden, _options]);

	const selectedOption = useMemo(
		() => options.find((option) => option.value === value),
		[value, options]
	);

	const isEmptyList = useMemo(
		() => options.length <= 1 && selectedOption,
		[options, selectedOption]
	);

	const onWindowClick = () => {
		setIsOpen(false);
	};

	const onWindowEsc = (event: KeyboardEvent) => {
		if (event.key === 'Escape') setIsOpen(false);
	};

	const onInputClick: MouseEventHandler<HTMLDivElement> = () => {
		if (!isOpen && isEmptyList) return;
		setTimeout(() => setIsOpen(!isOpen), 50);
	};

	useEffect(() => {
		listRef.current?.cleanSearch();

		if (isOpen) {
			window?.addEventListener('click', onWindowClick);
			window?.addEventListener('keydown', onWindowEsc);
		} else {
			window?.removeEventListener('click', onWindowClick);
			window?.removeEventListener('keydown', onWindowEsc);
		}

		return () => {
			window?.removeEventListener('click', onWindowClick);
			window?.removeEventListener('keydown', onWindowEsc);
		};
	}, [isOpen]);

	const mainClassNames = useMemo(() => {
		return classNames(styles.root, 'custom-select', {
			[styles.selectOpened]: isOpen,
			[styles.selectError]: error,
			[styles.selectEmpty]: isEmptyList,
		});
	}, [isOpen, error, isEmptyList]);

	return (
		<div className={classNames([mainClassNames], className)}>
			{typeof label === 'string' && <div className={styles.label}>{label}</div>}
			{typeof label === 'object' && label}

			<div className={classNames(['custom-select__input', styles.input])} onClick={onInputClick}>
				<Button align={'fill'} size={size} className={styles.head} fullWidth withShadow>
					<div className={styles.labelWrapper}>
						{selectedOption?.labelInHead ||
							selectedOption?.labelJSX ||
							selectedOption?.label ||
							placeholder}
						{subLabel && <div className={styles.subLabel}>{subLabel}</div>}
					</div>
					<div className={styles.headArrow}>
						<ArrowDownIcon size={24} />
					</div>
				</Button>
				{error && <span className={styles.error}>{error}</span>}
				<List
					ref={listRef}
					centerOptions={centerOptions}
					position={position}
					searchable={searchable}
					options={options}
					label={label}
					value={value}
					name={name}
					isOpen={isOpen}
					onChangeOpenState={setIsOpen}
					onChange={onChange}
				/>
			</div>
		</div>
	);
};
